import { Button } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { ClosingTabProps } from "features/closings/tabs/ClosingTabProps";
import {
  useClosingListSuspense,
  useClosingToApprovalUpdate,
  useClosingToCompleteUpdate,
} from "gen/endpoints/closing/closing";
import { getFundFamilyRetrieveQueryKey } from "gen/endpoints/fund-family/fund-family";
import { useFundFamilyFundraisingUpdate } from "gen/endpoints/fundraise/fundraise";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageTabs } from "shared/components/layout/PageTabs";
import { useToast } from "shared/components/toast/useToast";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const FundFamilySetupClosingPage: React.FC = () => {
  const { t } = useTranslation(["common", "closing"]);
  const { showToast, showErrorToast } = useToast();
  const navigate = useNavigate();

  const { fundFamilyId } = useRequiredStringParams();

  const { data: closings } = useClosingListSuspense(fundFamilyId, {
    is_draft: true,
  });

  const queryClient = useQueryClient();

  const {
    mutateAsync: sendClosingForApproval,
    isPending: isSendClosingForApprovalLoading,
  } = useClosingToApprovalUpdate();
  const { mutateAsync: completeClosing, isPending: isCompleteClosingLoading } =
    useClosingToCompleteUpdate();
  const {
    mutateAsync: moveFundFamilyToFundraising,
    isPending: fundraisingFundFamilyLoading,
  } = useFundFamilyFundraisingUpdate();

  const isFinalizing =
    isSendClosingForApprovalLoading ||
    isCompleteClosingLoading ||
    fundraisingFundFamilyLoading;

  if (closings.length === 0) {
    throw new Error("Default closing not found");
  } else if (closings.length > 1) {
    throw new Error("Multiple closings found");
  }

  const closing = closings[0];
  const closingId = closing.id!;

  const tabProps: ClosingTabProps = { fundFamilyId, closingId };

  const onClickFinalize = useCallback(async () => {
    try {
      await sendClosingForApproval({ fundFamilyId, closingId });
      await completeClosing({ fundFamilyId, closingId });
      await moveFundFamilyToFundraising({ fundFamilyId });
      await queryClient.invalidateQueries({
        queryKey: getFundFamilyRetrieveQueryKey(fundFamilyId),
      });

      showToast({
        message: t("common:completed"),
        variant: "success",
      });

      navigate(rootRoute({}).fundFamilies({}).fundFamily({ fundFamilyId }).$);
    } catch (err) {
      showErrorToast(err);
    }
  }, [
    closingId,
    completeClosing,
    moveFundFamilyToFundraising,
    fundFamilyId,
    navigate,
    sendClosingForApproval,
    showErrorToast,
    showToast,
    queryClient,
    t,
  ]);

  return (
    <PageContent>
      <PageHeader
        title={t("common:closing")}
        actions={
          <Button isLoading={isFinalizing} onClick={onClickFinalize}>
            {t("closing:finalize")}
          </Button>
        }
      />
      <PageTabs context={tabProps} />
    </PageContent>
  );
};
